import { Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Button, ButtonDirective } from 'primeng/button';
import { Card } from 'primeng/card';

import { environment } from '../../../environments/environment';
import { CompanyContextService } from '../../services/company-context.service';
import { LicenseContextService } from '../../services/license-context.service';
import { ModuleRegistryService } from '../../services/module-registry.service';

/**
 * Shown instead of a module the viewer may not open because of licensing, at
 * `/modules/no-licence/:moduleId`.
 *
 * Before this page the shell bounced such a navigation to the dashboard without a word — from the
 * sidebar, a link, or another module's "open" button alike — and the person could not tell a
 * missing licence from a broken link. Here it says which company lacks what, and where to get it:
 * licences are bought and seats assigned in UMC, never in the shell.
 *
 * Two cases, from `licenseStateOf`: the company holds no licence (`unlicensed`), or it holds one
 * but this person has no seat on it (`noSeat`) — a different sentence, since only an administrator
 * can fix the second.
 */
@Component({
  selector: 'app-module-no-licence',
  imports: [Card, Button, ButtonDirective, RouterLink, TranslatePipe],
  template: `
    <p-card [header]="'module.noLicence.title' | translate: { module: moduleTitle() | translate }">
      <p>
        {{
          (state() === 'noSeat' ? 'module.noLicence.noSeat' : 'module.noLicence.unlicensed')
            | translate: { module: moduleTitle() | translate, company: companyName() }
        }}
      </p>
      <p>{{ 'module.noLicence.whereToGet' | translate }}</p>

      <div class="no-licence__actions">
        @if (umcLicensesUrl) {
          <a pButton [href]="umcLicensesUrl" target="_blank" rel="noopener">
            <i class="pi pi-external-link" aria-hidden="true"></i>
            {{ 'module.noLicence.openUmc' | translate }}
          </a>
        }
        <p-button
          routerLink="/"
          [label]="'module.back' | translate"
          icon="pi pi-arrow-left"
          severity="secondary"
          [outlined]="true"
        />
      </div>
    </p-card>
  `,
  styles: `
    :host {
      display: block;
      max-width: 40rem;
      margin-inline: auto;
      padding: 2rem 1.5rem;
    }

    .no-licence__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-top: 1rem;
    }
  `,
})
export class ModuleNoLicence {
  /** Bound from the `:moduleId` route parameter. */
  readonly moduleId = input.required<string>();

  private readonly registry = inject(ModuleRegistryService);
  private readonly licenses = inject(LicenseContextService);
  private readonly companies = inject(CompanyContextService);

  /** UMC's licence screen; absent when the environment names no UMC console. */
  protected readonly umcLicensesUrl = umcLicensesUrl(environment.links?.umcConsoleUrl);

  /** The module's title key, or its bare id when the registry does not know it. */
  protected readonly moduleTitle = computed(() => {
    const module = this.registry.allModules().find((entry) => entry.id === this.moduleId());
    return module?.manifest?.titleKey ?? this.moduleId();
  });

  protected readonly state = computed(() => this.licenses.licenseStateOf(this.moduleId()));

  protected readonly companyName = computed(
    () => this.companies.activeMembership()?.companyName ?? '',
  );
}

/** `<console>/licenses`, or null without a console address. Pure, for the spec. */
export function umcLicensesUrl(consoleUrl: string | undefined): string | null {
  const base = consoleUrl?.replace(/\/+$/, '');
  if (!base) {
    return null;
  }
  return `${base}/licenses`;
}
